/**
 * GalleryStatusBadge - Unified status display component.
 *
 * Provides consistent status visualization across all gallery views.
 * Uses the unified status color tokens.
 *
 * @see /src/features/gallery/utils/statusColors.ts - Color tokens
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react'
import {
  getStatusBadgeVariant,
  getStatusLabel,
  getStatusColors,
} from '@/features/gallery/utils/statusColors'
import type { PhotoReviewStatus, ReviewStatus } from '@/types/gallery'

interface GalleryStatusBadgeProps {
  /** Status value */
  status: PhotoReviewStatus | ReviewStatus | string | null | undefined
  /** Show icon alongside text */
  showIcon?: boolean
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Additional className */
  className?: string
}

/**
 * Icon mapping for statuses.
 */
const STATUS_ICONS = {
  approved: CheckCircle,
  rejected: XCircle,
  pending: Clock,
  under_review: AlertTriangle,
} as const

/**
 * Get icon component for a status.
 */
function getStatusIcon(status: string | null | undefined) {
  if (!status) return Clock
  return STATUS_ICONS[status as keyof typeof STATUS_ICONS] ?? Clock
}

/**
 * Badge component with consistent status styling.
 */
export function GalleryStatusBadge({
  status,
  showIcon = true,
  size = 'md',
  className,
}: GalleryStatusBadgeProps) {
  const variant = getStatusBadgeVariant(status)
  const label = getStatusLabel(status)
  const Icon = getStatusIcon(status)

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-xs px-2 py-1',
    lg: 'text-sm px-2.5 py-1',
  }

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  }

  return (
    <Badge
      variant={variant}
      className={cn(
        'font-medium gap-1 inline-flex items-center',
        sizeClasses[size],
        className
      )}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      {label}
    </Badge>
  )
}

/**
 * Compact status dot indicator for thumbnails.
 * Just a colored circle, no text.
 */
export function StatusDot({
  status,
  size = 'md',
  className,
}: {
  status: string | null | undefined
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const colors = getStatusColors(status)

  const sizeClasses = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  }

  return (
    <span
      className={cn('rounded-full', colors.bgSolid, sizeClasses[size], className)}
      role="img"
      aria-label={getStatusLabel(status)}
    />
  )
}

/**
 * Status indicator with background highlight.
 * More prominent than badge, used for key status displays.
 */
export function StatusIndicator({
  status,
  showIcon = true,
  className,
}: Omit<GalleryStatusBadgeProps, 'size'>) {
  const colors = getStatusColors(status)
  const label = getStatusLabel(status)
  const Icon = getStatusIcon(status)

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-3 py-2 rounded-lg border',
        colors.bg,
        colors.border,
        className
      )}
    >
      {showIcon && <Icon className={cn('h-4 w-4', colors.text)} />}
      <span className={cn('text-sm font-medium', colors.text)}>{label}</span>
    </div>
  )
}
